import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { vesselsApi } from './service';
import type { VesselFormValues, VesselUpdateValues } from './schema';
import type { Vessel } from './types';

/**
 * Query key factory — collocated with the hooks so refactoring a key
 * touches a single location.
 *
 *   vesselsKeys.all              every cached vessels query
 *   vesselsKeys.lists()          all list queries (future: with filters)
 *   vesselsKeys.detail(id)       single vessel detail
 */
export const vesselsKeys = {
  all: ['vessels'] as const,
  lists: () => [...vesselsKeys.all, 'list'] as const,
  detail: (id: string) => [...vesselsKeys.all, 'detail', id] as const,
};

export function useVessels() {
  return useQuery({
    queryKey: vesselsKeys.lists(),
    queryFn: vesselsApi.list,
  });
}

export function useVessel(id: string | undefined) {
  return useQuery({
    queryKey: id ? vesselsKeys.detail(id) : vesselsKeys.all,
    queryFn: () => vesselsApi.getById(id as string),
    enabled: Boolean(id),
  });
}

/**
 * Mutations follow the simple pattern decided for F3 (no optimistic
 * updates). On success they invalidate the affected list/detail keys
 * so the next reader fetches fresh data; the offline-aware variants
 * land in F12.
 */
export function useCreateVessel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: VesselFormValues) => vesselsApi.create(input),
    onSuccess: (created: Vessel) => {
      qc.invalidateQueries({ queryKey: vesselsKeys.lists() });
      qc.setQueryData(vesselsKeys.detail(created.id), created);
    },
  });
}

export function useUpdateVessel(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: VesselUpdateValues) => vesselsApi.update(id, patch),
    onSuccess: (updated: Vessel) => {
      qc.invalidateQueries({ queryKey: vesselsKeys.lists() });
      qc.setQueryData(vesselsKeys.detail(id), updated);
    },
  });
}

export function useDeleteVessel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => vesselsApi.remove(id),
    onSuccess: (_void, id) => {
      qc.invalidateQueries({ queryKey: vesselsKeys.lists() });
      qc.removeQueries({ queryKey: vesselsKeys.detail(id) });
    },
  });
}
